import { IEltCustomerRevenue } from 'legacy/lut/classes/models/partner360/ELT/docEltType/IEltCustomerRevenue'
import { CUSTOMER_REVENUE_TOTAL } from 'legacy/lut/components/widgets/public/partner360/ELT/configs'
import { getPercentDiff } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getPercentDiff'
import { getPostfixByActualsValueType } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getPostfixByActualsValueType'

import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

type IYearsConfigItem = {
  currentYearField: string
  prevYearField: string
  twoYearsAgoField: string
  yoyField: string
}

const getCustomerRevenueFieldsByValueTypes = (
  actualValueTypes: ActualsValueTypes[]
): IYearsConfigItem[] => {
  return actualValueTypes.map((valueType) => {
    const postfix = getPostfixByActualsValueType(valueType)

    return {
      currentYearField: `CurYearTotal${postfix}`,
      prevYearField: `PrevYearTotal${postfix}`,
      twoYearsAgoField: `TwoYearsAgoTotal${postfix}`,
      yoyField: `YoY${postfix}`,
    }
  })
}

const getNumericFieldValue = <T extends object>(item: T, field: string): number => {
  return (item[field as keyof T] as number | null | undefined) ?? 0
}

const setNumericFieldValue = <T extends object>(
  item: T,
  field: string,
  value: number | null
): T => {
  return {
    ...item,
    [field]: value,
  }
}

export const getCustomerRevenueInitialData = (
  data: IEltCustomerRevenue[],
  actualValueTypes: ActualsValueTypes[]
): IEltCustomerRevenue[] => {
  const fieldsConfig = getCustomerRevenueFieldsByValueTypes(actualValueTypes)

  const result = data
    .map((item) => {
      return fieldsConfig.reduce<IEltCustomerRevenue>((acc, fieldConfig) => {
        const currentYearValue = getNumericFieldValue(acc, fieldConfig.currentYearField)
        const prevYearValue = getNumericFieldValue(acc, fieldConfig.prevYearField)

        return setNumericFieldValue(
          acc,
          fieldConfig.yoyField,
          getPercentDiff(currentYearValue, prevYearValue)
        )
      }, item)
    })
    .sort((a, b) => {
      const aOrder = a.OrderBy ?? Infinity
      const bOrder = b.OrderBy ?? Infinity

      return aOrder - bOrder
    })

  const total = result.reduce<IEltCustomerRevenue>(
    (acc, curr) => {
      return fieldsConfig.reduce<IEltCustomerRevenue>((fieldAcc, fieldConfig) => {
        const twoYearsAgoTotal =
          getNumericFieldValue(fieldAcc, fieldConfig.twoYearsAgoField) +
          getNumericFieldValue(curr, fieldConfig.twoYearsAgoField)

        const prevYearTotal =
          getNumericFieldValue(fieldAcc, fieldConfig.prevYearField) +
          getNumericFieldValue(curr, fieldConfig.prevYearField)

        const currentYearTotal =
          getNumericFieldValue(fieldAcc, fieldConfig.currentYearField) +
          getNumericFieldValue(curr, fieldConfig.currentYearField)

        return {
          ...fieldAcc,
          [fieldConfig.twoYearsAgoField]: twoYearsAgoTotal,
          [fieldConfig.prevYearField]: prevYearTotal,
          [fieldConfig.currentYearField]: currentYearTotal,
        }
      }, acc)
    },
    {
      TranslationKey: CUSTOMER_REVENUE_TOTAL,
    } as IEltCustomerRevenue
  )

  const totalWithYoY = fieldsConfig.reduce<IEltCustomerRevenue>((acc, fieldConfig) => {
    const currentYearValue = getNumericFieldValue(acc, fieldConfig.currentYearField)
    const prevYearValue = getNumericFieldValue(acc, fieldConfig.prevYearField)

    return setNumericFieldValue(
      acc,
      fieldConfig.yoyField,
      getPercentDiff(currentYearValue, prevYearValue)
    )
  }, total)

  return [...result, totalWithYoY]
}
